import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns/format';
import { parseISO } from 'date-fns/parseISO';
import axios from 'axios';
import WizardContainer from '../../components/Wizard/WizardContainer';
import { ApplicationStatus } from '../../types/wizard';
import { usePageTracking, useWizardStepTracking, useStepTimer, useModalTracking, useAnalytics } from '../../hooks/useAnalytics';
import { courierApiUrl } from '../../config';

interface ScheduleInterviewProps {
  applicationStatus: ApplicationStatus;
}

interface InterviewSlot {
  start: string;
  end: string;
  displayTime: string;
}

type BookingStatus = 'idle' | 'loading' | 'ready' | 'booking' | 'success' | 'error';

const ScheduleInterview: React.FC<ScheduleInterviewProps> = ({ applicationStatus }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || searchParams.get('email') || '';

  const [status, setStatus] = useState<BookingStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [availableSlots, setAvailableSlots] = useState<InterviewSlot[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<InterviewSlot | null>(null);
  const [groupedSlots, setGroupedSlots] = useState<{ [date: string]: InterviewSlot[] }>({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  usePageTracking('Schedule Interview');
  useWizardStepTracking('schedule-interview', 5, 7);
  const { getTimeSpent } = useStepTimer();
  const { onOpen: onConfirmOpen, onClose: onConfirmClose } = useModalTracking('interview_confirmation');
  const { trackInterviewScheduled, trackWizardStepComplete, trackAPIError } = useAnalytics();

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('No token provided. Please use the link from your email.');
      return;
    }

    loadAvailableSlots();
  }, [token]);

  const loadAvailableSlots = async () => {
    if (!token) return;

    setStatus('loading');
    try {
      const response = await axios.get(
        courierApiUrl('/interview/available-times'),
        { params: { token } }
      );

      if (response.data.error) {
        setStatus('error');
        setErrorMessage(response.data.error);
      } else if (!response.data.availableSlots || response.data.availableSlots.length === 0) {
        setStatus('error');
        setErrorMessage('No interview slots are currently available. Please check back later or contact support.');
      } else {
        // Add displayTime to each slot and convert to EST
        const slotsWithDisplay = response.data.availableSlots.map((slot: any) => {
          const startTime = parseISO(slot.start);
          const endTime = parseISO(slot.end);

          // Format like Google Calendar: "9:00 AM" or "2:30 PM"
          const displayTime = format(startTime, 'h:mm a');

          return {
            ...slot,
            displayTime,
          };
        });

        setAvailableSlots(slotsWithDisplay);

        // Group slots by date
        const grouped: { [date: string]: InterviewSlot[] } = {};
        slotsWithDisplay.forEach((slot: InterviewSlot) => {
          const dateKey = format(parseISO(slot.start), 'EEEE, MMMM d, yyyy');
          if (!grouped[dateKey]) {
            grouped[dateKey] = [];
          }
          grouped[dateKey].push(slot);
        });
        setGroupedSlots(grouped);

        setStatus('ready');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('Failed to load available times. Please try again.');
    }
  };

  const handleSelectSlot = (slot: InterviewSlot) => {
    setSelectedSlot(slot);
    setErrorMessage('');
    onConfirmOpen('slot_selection');
    setShowConfirmModal(true);
  };

  const handleBookInterview = async () => {
    if (!selectedSlot || !token) return;

    setShowConfirmModal(false);
    onConfirmClose('confirm');
    setStatus('booking');
    setErrorMessage('');

    try {
      const response = await axios.post(
        courierApiUrl('/interview/book-time'),
        {
          token,
          dateTime: selectedSlot.start,
        }
      );

      if (response.data.success) {
        trackInterviewScheduled({ selectedDate: format(parseISO(selectedSlot.start), 'yyyy-MM-dd'), selectedTime: selectedSlot.displayTime, bookingSource: 'wizard' });
        trackWizardStepComplete({ stepName: 'schedule-interview', timeOnStep: getTimeSpent(), completionStatus: 'success' });
        setStatus('success');
        // Navigate to interview-scheduled page after 2 seconds
        setTimeout(() => {
          navigate(`/wizard/interview-scheduled?token=${token}`);
        }, 2000);
      } else {
        setStatus('error');
        setErrorMessage(response.data.error || 'Failed to book interview. Please try again.');
      }
    } catch (error) {
      trackAPIError({ endpoint: '/api/interview/book-time', errorCode: 'unknown', errorMessage: 'Failed to book interview' });
      setStatus('error');
      setErrorMessage('An unexpected error occurred. Please try again.');
    }
  };

  return (
    <WizardContainer
      currentStepId="schedule-interview"
      applicationStatus={applicationStatus}
    >
      <div className="max-w-4xl mx-auto">
        <div className="card p-6 sm:p-8">
          <h1 className="text-2xl font-bold text-charcoal mb-2">Schedule Your Interview</h1>
          <p className="text-gray-600 mb-8">
            Select a time slot that works best for you. We'll send you a calendar invite once confirmed.
          </p>

          {status === 'loading' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-logo-green mx-auto mb-4"></div>
              <p className="text-gray-600">Loading available times...</p>
            </div>
          )}

          {status === 'error' && (
            <div className="bg-alert-red/10 border border-alert-red text-alert-red rounded-lg p-4 mb-6">
              <p className="font-semibold">Error</p>
              <p>{errorMessage}</p>
            </div>
          )}

          {status === 'ready' && (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-charcoal mb-4">Available Time Slots</h2>

                <div className="space-y-6">
                  {Object.entries(groupedSlots).map(([date, slots]) => (
                    <div key={date}>
                      <h3 className="text-lg font-semibold text-logo-green mb-3">{date}</h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                        {slots.map((slot, index) => (
                          <button
                            key={index}
                            onClick={() => handleSelectSlot(slot)}
                            className={`px-4 py-2 rounded-md border text-sm font-medium transition-all duration-150 ${
                              selectedSlot?.start === slot.start
                                ? 'border-logo-green bg-logo-green text-white shadow-sm'
                                : 'border-gray-200 bg-white text-gray-700 hover:border-logo-green hover:bg-gray-50'
                            }`}
                          >
                            {slot.displayTime}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {status === 'booking' && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-logo-green mx-auto mb-4"></div>
              <p className="text-gray-600">Booking your interview...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-logo-green/10 rounded-full mb-4">
                <svg className="w-8 h-8 text-logo-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-charcoal mb-2">Interview Scheduled!</h2>
              <p className="text-gray-600 mb-4">
                Your interview has been successfully scheduled for:
              </p>
              {selectedSlot && (
                <div className="bg-logo-green/10 border border-logo-green rounded-lg p-4 mb-6 inline-block">
                  <p className="font-semibold text-charcoal text-lg">
                    {format(parseISO(selectedSlot.start), 'EEEE, MMMM d, yyyy')}
                  </p>
                  <p className="text-charcoal">{selectedSlot.displayTime}</p>
                </div>
              )}
              <p className="text-sm text-gray-500">
                Redirecting to next step...
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && selectedSlot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-4 sm:p-6">
            <h3 className="text-xl font-bold text-charcoal mb-4">Confirm Interview Time</h3>

            <div className="bg-logo-green/10 border border-logo-green rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600 mb-1">You selected:</p>
              <p className="font-semibold text-charcoal text-lg">
                {format(parseISO(selectedSlot.start), 'EEEE, MMMM d, yyyy')}
              </p>
              <p className="text-charcoal text-lg">{selectedSlot.displayTime}</p>
            </div>

            <p className="text-gray-600 text-sm mb-6">
              We'll send you a calendar invite to the email address you provided. The interview will be conducted via video call.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => { onConfirmClose('cancel'); setShowConfirmModal(false); }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleBookInterview}
                className="flex-1 btn-primary"
              >
                Confirm Booking
              </button>
            </div>
          </div>
        </div>
      )}
    </WizardContainer>
  );
};

export default ScheduleInterview;
